import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import api, { ApiResponse, toSessionUser } from './api';
import type { User } from '../stores/auth.store';

export interface Passkey {
  id: string;
  name: string;
  backed_up: boolean;
  device_type: 'singleDevice' | 'multiDevice';
  created_at: string;
  last_used_at: string | null;
}

interface LoginResponse {
  accessToken: string;
  expiresIn: number;
  user: Parameters<typeof toSessionUser>[0];
}

/** The user dismissed the OS prompt, or had nothing to offer. Not a failure worth
 *  colouring red — they simply chose another way in. */
export function isDismissal(err: unknown): boolean {
  return err instanceof Error && (err.name === 'NotAllowedError' || err.name === 'AbortError');
}

export const passkeySupported = browserSupportsWebAuthn;
export const passkeyAutofillSupported = browserSupportsWebAuthnAutofill;

// The browser will not say whether it holds a key for us — that would let any page
// enumerate who has an account here — so this note is the only signal there is.
const DEVICE_HINT = 'mx-passkey-on-this-device';

export function hasPasskeyOnThisDevice(): boolean {
  try {
    return localStorage.getItem(DEVICE_HINT) === '1';
  } catch {
    return false;
  }
}

function rememberDevice(remembered: boolean): void {
  try {
    if (remembered) localStorage.setItem(DEVICE_HINT, '1');
    else localStorage.removeItem(DEVICE_HINT);
  } catch {
    // Private windows and blocked site data. The flow works without the shortcut.
  }
}

export const forgetPasskeyOnThisDevice = () => rememberDevice(false);

export const passkeyService = {
  enroll: async (name: string): Promise<Passkey> => {
    const options = await api.post<ApiResponse<PublicKeyCredentialCreationOptionsJSON>>(
      '/auth/passkey/register/options'
    );
    const credential = await startRegistration({ optionsJSON: options.data.data! });
    const saved = await api.post<ApiResponse<Passkey>>('/auth/passkey/register/verify', {
      challenge: options.data.data!.challenge,
      credential,
      name,
    });
    rememberDevice(true);
    return saved.data.data!;
  },

  /** `autofill` runs the ceremony conditionally: finding no key of ours, the browser
   *  does nothing at all and the promise never settles. */
  signIn: async ({ autofill = false } = {}): Promise<{ user: User; accessToken: string }> => {
    const options = await api.post<ApiResponse<PublicKeyCredentialRequestOptionsJSON>>(
      '/auth/passkey/login/options'
    );
    const credential = await startAuthentication({
      optionsJSON: options.data.data!,
      useBrowserAutofill: autofill,
    });
    const session = await api.post<ApiResponse<LoginResponse>>('/auth/passkey/login/verify', {
      challenge: options.data.data!.challenge,
      credential,
    });
    const data = session.data.data!;
    rememberDevice(true);
    return { user: toSessionUser(data.user), accessToken: data.accessToken };
  },

  list: async (): Promise<Passkey[]> => {
    const response = await api.get<ApiResponse<Passkey[]>>('/auth/passkeys');
    return response.data.data!;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/auth/passkeys/${id}`);
  },
};
